import { Minus, Pause, Play, Settings, Volume2, X } from "lucide-react";
import { useState } from "react";
import { useVoice } from "../../contexts/VoiceContext";
import { useShowVoiceButton } from "../../hooks/useUserPreferences";
import { Button } from "./button";
import { Card } from "./card";

interface VoiceControlBarProps {
    onOpenSettings?: () => void;
}

export function VoiceControlBar({ onOpenSettings }: VoiceControlBarProps) {
    const { state, pause, resume, stop } = useVoice();
    const [isMinimized, setIsMinimized] = useState(false);
    const showVoiceButton = useShowVoiceButton();

    // Only show when there's an active voice session and user has voice buttons enabled
    if (!showVoiceButton || !state.isSupported || !state.isActive || !state.currentText) {
        return null;
    }

    const truncatedText =
        state.currentText.length > 60 ? `${state.currentText.substring(0, 60)}...` : state.currentText;

    const handleClose = () => {
        stop();
    };

    const handleTogglePlay = () => {
        if (state.isPlaying) {
            pause();
        } else if (state.isPaused) {
            resume();
        }
    };

    if (isMinimized) {
        return (
            <div className="fixed bottom-20 md:bottom-4 right-4 z-50">
                <div className="flex page-gradient rounded-full shadow-lg border border-border">
                    <Button
                        variant="default"
                        size="icon"
                        onClick={handleTogglePlay}
                        className="h-14 w-14 rounded-full"
                        style={{ backgroundColor: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
                        title={state.isPlaying ? "Pause" : "Play"}
                    >
                        {state.isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsMinimized(false)}
                        className="h-14 w-10 rounded-full hover:bg-primary/10 hover:text-primary"
                        title="Expand controls"
                    >
                        <Minus className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed bottom-20 md:bottom-4 right-4 z-50 max-w-sm">
            <Card className="p-4 shadow-lg page-gradient md:bg-background border border-border md:border-primary/20">
                <div className="space-y-3">
                    {/* Header with minimize and close */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Volume2 className="h-4 w-4" />
                            <span className="text-sm font-medium">Now Reading</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsMinimized(true)}
                                className="h-6 w-6"
                                title="Minimize"
                            >
                                <div className="h-0.5 w-3 bg-current" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleClose}
                                className="h-6 w-6"
                                title="Stop and close"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>

                    {/* Current text */}
                    <div className="text-sm text-muted-foreground leading-relaxed">{truncatedText}</div>

                    {/* Controls */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleTogglePlay}
                                className="h-8 w-8"
                                title={state.isPlaying ? "Pause" : "Resume"}
                            >
                                {state.isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="icon" onClick={stop} className="h-8 w-8" title="Stop">
                                <div className="h-3 w-3 bg-current rounded-sm" />
                            </Button>
                        </div>

                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onOpenSettings}
                                className="h-8 w-8"
                                title="Voice settings"
                            >
                                <Settings className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}
